/**
 * 考试API函数
 * 提供获取考试列表、考试详情、计算题目数和时长等操作
 */
import { supabase } from '../supabaseClient';
import { 
  getSubjectsByExamId, 
  getSubjectQuestionConfig, 
  parseTypeCount 
} from './subjectsAPI';
// import { getTypeName } from './questionAPI'; // 未使用
// import { mapTypeIdToName } from './questionTypesMapAPI'; // 未使用
import { getQuestions } from './questionAPI';

/**
 * 获取所有考试列表
 * @param {object} options - 查询选项
 * @returns {Promise<Array>} 考试列表
 */
export const getAllExams = async (options = {}) => {
  try {
    const { limit, orderBy = '_id', ascending = false } = options;
    
    let query = supabase
      .from('exams')
      .select('*')
      .order(orderBy, { ascending });
      
    if (limit) {
      query = query.limit(limit);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('获取考试列表出错:', error);
    throw error;
  }
};

/**
 * 获取考试详情
 * @param {number} examId - 考试ID
 * @returns {Promise<Object>} 考试详情
 */
export const getExamById = async (examId) => {
  try {
    const { data, error } = await supabase
      .from('exams')
      .select('*')
      .eq('_id', Number(examId))
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('获取考试详情出错:', error);
    throw error;
  }
};

/**
 * 获取考试的所有科目
 * @param {number} examId - 考试ID
 * @returns {Promise<Array>} 科目列表
 */
export const getExamSubjects = async (examId) => {
  return getSubjectsByExamId(examId);
};

/**
 * 计算考试总题目数
 * @param {number} examId - 考试ID
 * @returns {Promise<number|null>} 题目总数或null
 */
export const calculateTotalQuestions = async (examId) => {
  try {
    // 先从subjects表获取该考试下的所有科目
    const subjects = await getExamSubjects(examId);
    
    let totalCount = 0;
    
    // 遍历每个科目，计算题目总数
    for (const subject of subjects || []) {
      // 首先检查subject_question配置
      const subjectQuestions = await getSubjectQuestionConfig(subject._id);
        
      if (subjectQuestions && subjectQuestions.length > 0) {
        // 使用subject_question表的配置计算题目数
        const subjectTotal = subjectQuestions.reduce((sum, sq) => sum + sq.question_count, 0);
        totalCount += subjectTotal;
      } else if (subject.type_count) {
        // 尝试使用type_count字段
        try {
          const typeCount = parseTypeCount(subject.type_count);
          const subjectTotal = Object.values(typeCount).reduce((sum, count) => sum + count, 0);
          totalCount += subjectTotal;
        } catch (e) {
          console.error('解析type_count出错:', e);
          if (subject.count) {
            totalCount += subject.count;
          }
        }
      } else if (subject.count) {
        // 使用科目自己的count字段
        totalCount += subject.count;
      }
    }
    
    return totalCount > 0 ? totalCount : null;
  } catch (error) {
    console.error('计算题目总数出错:', error);
    return null;
  }
};

/**
 * 计算考试总时长
 * @param {number} examId - 考试ID
 * @returns {Promise<number|null>} 总时长(分钟)或null
 */
export const calculateTotalDuration = async (examId) => {
  try {
    // 获取该考试下的所有科目
    const subjects = await getExamSubjects(examId);
    
    // 计算所有科目的时长总和
    const totalDuration = (subjects || []).reduce((sum, subject) => {
      return sum + (subject.duration || 0);
    }, 0);
    
    return totalDuration > 0 ? totalDuration : null;
  } catch (error) {
    console.error('计算总时长出错:', error);
    return null;
  }
};

/**
 * 获取考试信息，包括题目总数和时长
 * @param {number} examId - 考试ID
 * @returns {Promise<Object>} 考试信息，包括题目总数和时长
 */
export const getExamInfo = async (examId) => {
  try {
    const exam = await getExamById(examId);
    
    if (!exam) {
      throw new Error('考试不存在');
    }
    
    // 获取题目总数
    let totalQuestions = exam.count;
    if (!totalQuestions) {
      totalQuestions = await calculateTotalQuestions(examId);
    }
    
    // 获取总时长
    let totalDuration = exam.duration;
    if (!totalDuration) {
      totalDuration = await calculateTotalDuration(examId);
    }
    
    return {
      ...exam,
      totalQuestions,
      totalDuration
    };
  } catch (error) {
    console.error('获取考试信息出错:', error);
    throw error;
  }
};

/**
 * 获取多个考试的题目总数和时长
 * @param {Array} exams - 考试列表
 * @returns {Promise<Object>} 包含题目总数和时长的对象
 */
export const getExamsInfoBatch = async (exams) => {
  try {
    const totals = {};
    const durations = {};
    
    for (const exam of exams || []) {
      // 题目总数
      if (exam.count) {
        totals[exam._id] = exam.count;
      } else {
        const total = await calculateTotalQuestions(exam._id);
        if (total) {
          totals[exam._id] = total;
        }
      }
      
      // 总时长
      if (exam.duration) {
        durations[exam._id] = exam.duration;
      } else {
        const duration = await calculateTotalDuration(exam._id);
        if (duration) {
          durations[exam._id] = duration;
        }
      }
    }
    
    return { totals, durations };
  } catch (error) {
    console.error('批量获取考试信息出错:', error);
    throw error;
  }
};

/**
 * 获取考试类型的统计信息（科目数量和题目总数）
 * @param {number} examId - 考试ID
 * @returns {Promise<Object>} 包含科目数量和题目总数的对象
 */
export const getExamStats = async (examId) => {
  try {
    // 获取科目数量
    const { count: subjectCount, error: subjectError } = await supabase
      .from('subjects')
      .select('_id', { count: 'exact' })
      .eq('pid', Number(examId));
      
    if (subjectError) throw subjectError;
    
    // 获取题目总数
    const { data: subjects, error: subjectsError } = await supabase
      .from('subjects')
      .select('_id')
      .eq('pid', Number(examId));
      
    if (subjectsError) throw subjectsError;
    
    let totalQuestions = 0;
    
    if (subjects && subjects.length > 0) {
      // 使用题目API获取所有科目的题目
      for (const subject of subjects) {
        const questions = await getQuestions({ examId: subject._id });
        totalQuestions += questions.length;
      }
    }
    
    return {
      subjectCount: subjectCount || 0,
      questionCount: totalQuestions
    };
  } catch (error) {
    console.error('获取考试统计信息出错:', error);
    return { subjectCount: 0, questionCount: 0 };
  }
};

/**
 * 批量获取考试类型的统计信息
 * @param {Array} exams - 考试列表
 * @returns {Promise<Object>} 以考试ID为键的统计信息对象
 */
export const getExamsStatsBatch = async (exams) => {
  try {
    const stats = {};
    
    for (const exam of exams || []) {
      const examStats = await getExamStats(exam._id);
      stats[exam._id] = examStats;
    }
    
    return stats;
  } catch (error) {
    console.error('批量获取考试统计信息出错:', error);
    return {};
  }
}; 